import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        chords: 'Chords',
        exercises: 'Exercises',
        etudes: 'Etudes',
        theory: 'Theory',
        songs: 'Songs',
        tuner: 'Tuner',
        recorder: 'Recorder',
        drums: 'Drum Tracks',
        metronome: 'Metronome',
        tutor: 'AI Tutor',
        skills: 'Skill Tree',
        progress: 'Progress',
        premium: 'Premium',
        login: 'Login',
        register: 'Register',
        logout: 'Logout',
        dashboard: 'Dashboard',
        profile: 'Profile'
      },
      // Landing Page
      landing: {
        hero: {
          tagline: 'Built in Italy, hand-written in English and Italian',
          title: 'Learn guitar properly.',
          subtitle: 'From your first chord to polyphonic fingerstyle, with hand-written tabs and an AI tutor that never sleeps. Real English and real Italian, never machine-translated. No endless video courses, no childish gamification.',
          cta: 'Start Learning Free',
          ctaLogin: 'Sign In'
        },
        features: {
          title: 'What FretFlow gives you',
          chords: {
            title: 'Chord Library',
            desc: '209 chords with diagrams, fingerings, variants and barre options'
          },
          exercises: {
            title: 'Technique Exercises',
            desc: 'Spider, scales, picking, sweep — all with built-in metronome'
          },
          theory: {
            title: 'Music Theory',
            desc: '54 lessons from staff notation to reharmonisation'
          },
          tuner: {
            title: 'Chromatic Tuner',
            desc: 'Real-time pitch detection from the microphone'
          },
          recorder: {
            title: 'Multitrack Recorder',
            desc: 'Record your guitar, layer vocals and bass'
          },
          drums: {
            title: 'Backing & Drums',
            desc: 'Drum patterns + synth backing band + real audio stems on PD songs'
          }
        },
        differentiators: {
          title: 'Why FretFlow is different',
          italian: {
            title: 'Bilingual, hand-written',
            desc: 'Real English and real Italian, written by guitarists - never auto-translated'
          },
          fingerstyle: {
            title: 'Fingerstyle and classical',
            desc: '30 polyphonic tabs hand-written (melody + bass interwoven)'
          },
          maestro: {
            title: 'Maestro AI',
            desc: 'Your personal tutor answers any guitar question 24/7'
          },
          allInOne: {
            title: 'All in one app',
            desc: 'Tuner, metronome, chords, lessons, songs, recorder — not 5 separate apps'
          }
        },
        levels: {
          title: 'For Every Level',
          beginner: 'Beginner',
          intermediate: 'Intermediate',
          expert: 'Advanced',
          beginnerDesc: 'From your first chord to your first song',
          intermediateDesc: 'Scales, theory, fingerstyle, styles',
          expertDesc: 'Modes, reharmonisation, improvisation'
        },
        finalCta: {
          title: 'Ready to take it seriously?',
          subtitle: 'A real free tier — not a crippled trial. Premium €5.99/month or €49.99/year when you want it all.'
        },
        footer: {
          rights: 'All rights reserved.'
        }
      },
      progress: {
        tagline: 'Practice DNA',
        title: 'How you\'re evolving',
        subtitle: 'Skill coverage by category, BPM progression on technique exercises, 90-day streak and the songs in your repertoire.',
        loginRequired: 'Log in to see your progress.',
        streakNow: 'Current streak',
        streakLongest: 'Longest streak',
        lessonsDone: 'Lessons',
        songsPlayed: 'Songs played',
        cards: {
          radarTitle: 'Skill coverage',
          radarSubtitle: 'Percent of skills mastered per category',
          radarTooltip: 'mastered',
          bpmTitle: 'BPM progression',
          bpmSubtitle: 'Max + average BPM you hit on exercises (last 90 days)',
          bpmEmpty: 'No exercise BPM logged yet. Start an exercise and save your BPM.',
          bpmMax: 'Max',
          bpmAvg: 'Avg',
          heatmapTitle: 'Daily session streak',
          heatmapSubtitle: 'Each square = one day. Gold = today\'s session completed.',
          heatmapDays: 'days',
          heatmapLegendLess: 'Skipped',
          heatmapLegendMore: 'Done',
          repertoireTitle: 'Your repertoire',
          repertoireSubtitle: 'Songs you have started or completed',
          repertoireEmpty: 'Open a song to start your repertoire.',
          openLibrary: 'Library',
          repertoireStatus: {
            in_progress: 'Working on it',
            completed: 'Done',
            not_started: 'Not started'
          }
        }
      },
      skills: {
        tagline: 'Your skill tree',
        title: 'Where you stand on the fretboard',
        subtitle: 'A map of guitar skills, from your first chord to advanced harmony. Each lesson, exercise and song you complete unlocks the next nodes.',
        empty: 'No skills match this filter.',
        prerequisites: 'Prerequisites',
        howToMaster: 'Complete this skill by',
        skillsLabel: 'skills',
        filters: {
          all: 'All',
          available: 'Available',
          mastered: 'Mastered',
          locked: 'Locked',
          allCategories: 'All categories'
        },
        states: {
          mastered: 'Mastered',
          available: 'Available',
          locked: 'Locked'
        },
        stats: {
          mastered: 'Mastered',
          available: 'Available now',
          locked: 'Still locked'
        },
        difficulty: {
          beginner: 'Beginner',
          intermediate: 'Intermediate',
          advanced: 'Advanced'
        },
        categories: {
          fundamentals: 'Fundamentals',
          chords: 'Chords',
          rhythm: 'Rhythm',
          technique: 'Technique',
          scales: 'Scales',
          theory: 'Theory',
          style: 'Styles',
          repertoire: 'Repertoire'
        },
        types: {
          lesson: 'Lesson',
          exercise: 'Exercise',
          song: 'Song'
        }
      },
      session: {
        todayLabel: 'Today\'s session',
        minutes: 'minutes',
        minShort: 'min',
        done: 'done',
        open: 'Open',
        start: 'Start',
        freeplay: 'Play freely',
        markDone: 'Mark done',
        streakLabel: 'day streak',
        completedTitle: 'Session complete!',
        completedDesc: 'Streak at {{n}} days — come back tomorrow to keep it.',
        types: {
          warmup: 'Warm-up',
          lesson: 'Lesson',
          song: 'Song',
          freeplay: 'Free play'
        }
      },
      onboarding: {
        title: 'Welcome',
        step: 'Step {{current}} of {{total}}',
        back: 'Back',
        next: 'Next',
        finish: 'Take me to my dashboard',
        skip: 'Skip for now',
        levelTitle: 'What\'s your level?',
        levelSubtitle: 'We\'ll calibrate today\'s session around it. No wrong answer.',
        goalTitle: 'What do you want to get out of this?',
        goalSubtitle: 'Pick the one that fits today. You can change it later.',
        genresTitle: 'Which genres pull you in?',
        genresSubtitle: 'Pick at least one. Your daily song picks will reflect this.',
        chooseAtLeastOne: 'Pick at least one genre to continue',
        savingError: 'Could not save your preferences. Try again in a moment.',
        levels: {
          beginner: { label: 'Beginner', desc: 'I\'ve never played, or barely' },
          some_chords: { label: 'I know a few chords', desc: 'I can strum C, G, D, Am - not always cleanly' },
          intermediate: { label: 'Intermediate', desc: 'Barre chords, scales, a few songs' },
          advanced: { label: 'Advanced', desc: 'Modes, fingerstyle, improvisation' }
        },
        goals: {
          play_songs: { label: 'Sing my favourite songs', desc: 'Chords, strumming, sing along' },
          fingerstyle: { label: 'Play fingerstyle and classical', desc: 'Melody + bass on a single guitar' },
          improvise: { label: 'Improvise solos', desc: 'Scales, blues, rock, jazz vocabulary' },
          theory: { label: 'Understand music', desc: 'Theory, harmony, ear training' },
          mix: { label: 'A bit of everything', desc: 'Broad path with no fixed angle' }
        },
        genres: {
          pop: 'Pop / Rock',
          folk: 'Folk',
          fingerstyle: 'Fingerstyle',
          blues: 'Blues',
          jazz: 'Jazz',
          classical: 'Classical'
        }
      },
      // Common
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        search: 'Search',
        filter: 'Filter',
        refresh: 'Refresh',
        all: 'All',
        free: 'Free',
        premium: 'Premium',
        locked: 'Premium Only',
        unlock: 'Unlock with Premium'
      },
      // Levels
      levels: {
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        expert: 'Expert',
        advanced: 'Advanced',
        master: 'Master'
      },
      // Auth
      auth: {
        email: 'Email',
        password: 'Password',
        username: 'Username',
        login: 'Sign In',
        register: 'Create Account',
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        loginSuccess: 'Welcome back!',
        registerSuccess: 'Account created successfully!',
        failed: 'Authentication failed',
        warmupTitle: 'Server waking up',
        warmupDesc: 'Render\'s free tier sleeps after inactivity and takes 30-40 seconds to spin up the first time today. After that, it\'s instant.'
      },
      // Chords
      chords: {
        title: 'Chord Library',
        tagline: 'Chord library',
        subtitle: 'Diagrams, fingerings, variants and barre shapes for 209 chords. Click a chord to hear it with a real acoustic guitar.',
        realisticToggleTitle: 'Real acoustic guitar vs synth',
        realisticOn: 'Real',
        realisticOff: 'Synth',
        realisticLoading: 'Loading...',
        barre: 'Barre',
        listen: 'Listen',
        theoryNote: 'Theory note',
        fretBoardLayout: 'Strings (6 → 1)',
        fingers: 'Fingers',
        actionPlay: 'Play',
        actionStrumDown: 'Strum ↓',
        actionStrumUp: 'Strum ↑',
        theoryNotes: {
          major: 'Major triad: root + major 3rd + perfect 5th. Bright, "happy" sound. The backbone of pop and folk.',
          minor: 'Minor triad: root + minor 3rd + perfect 5th. Darker, melancholy character. Used everywhere from blues to classical.',
          '7th': 'Dominant 7th: triad + minor 7th on top. Wants to resolve — drives the V → I cadence at the heart of tonal music.',
          maj7: 'Major 7th: triad + major 7th. Smooth, jazzy, dreamy. The Steely Dan / bossa nova sound.',
          m7: 'Minor 7th: minor triad + minor 7th. Used as the ii chord in jazz ii-V-I and for warm minor key vamps.',
          sus: 'Suspended: the 3rd is replaced by a 2nd or 4th. Creates tension that wants to resolve back to the major chord.',
          dim: 'Diminished: minor triad with a flatted 5th. Restless, transitional — usually passes through to another chord.',
          aug: 'Augmented: major triad with a raised 5th. Symmetrical and unstable — divides the octave into equal thirds.',
          add: 'Add chord: the named extension is added without the 7th. Add9 keeps it open and airy.',
          power: 'Power chord: root + perfect 5th, no 3rd. Neither major nor minor — the rock and metal staple.'
        },
        categories: {
          major: 'Major',
          minor: 'Minor',
          '7th': 'Seventh',
          maj7: 'Major 7th',
          m7: 'Minor 7th',
          sus: 'Suspended',
          dim: 'Diminished',
          aug: 'Augmented'
        },
        strings: 'Strings',
        frets: 'Frets',
        finger: 'Finger',
        availableCount: '{{count}} chords available',
        searchPlaceholder: 'Search chords...',
        difficulty: 'Difficulty',
        rootNote: 'Note',
        category: 'Category',
        pageInfo: 'Page {{page}} of {{total}}',
        arpeggio: 'Arpeggio',
        proOnly: 'PRO only'
      },
      // Exercises
      exercises: {
        title: 'Technique Exercises',
        categories: {
          technique: 'Technique',
          speed: 'Speed',
          fingerpicking: 'Fingerpicking',
          chords: 'Chord Changes'
        },
        duration: '{{minutes}} min',
        start: 'Start Exercise',
        difficulty: 'Difficulty',
        category: 'Category',
        tabNotation: 'Tab notation:',
        instructions: 'Step-by-step instructions',
        tips: 'Tips',
        bpmStart: 'Start BPM',
        bpmTarget: 'Target BPM',
        noResults: 'No exercises match the current filters',
        completed: 'Exercise completed! ✓',
        saveLogin: 'Sign in to save your progress',
        saveError: 'Failed to save',
        relatedLesson: 'Related lesson'
      },
      // Theory
      theory: {
        title: 'Music Theory Lessons',
        categories: {
          basics: 'Basics',
          fundamentals: 'Fundamentals',
          scales: 'Scales',
          harmony: 'Harmony',
          modes: 'Modes',
          rhythm: 'Rhythm',
          chords: 'Chords',
          technique: 'Technique',
          theory: 'Theory topic',
          style: 'Style',
          history: 'History',
          posture: 'Posture',
          improvisation: 'Improvisation',
          composition: 'Composition'
        },
        subjects: {
          history: 'History',
          theory: 'Music theory',
          technique: 'Technique',
          style: 'Styles and repertoire',
          gear: 'Gear and setup',
          practice: 'Practice and method'
        },
        difficulty: 'Difficulty',
        subject: 'Subject',
        category: 'Category',
        noResults: 'No lessons match the current filters',
        completed: 'Lesson completed! ✓',
        saveLogin: 'Sign in to save your progress',
        saveError: 'Failed to save',
        duration: '{{minutes}} min',
        objectives: 'Learning objectives',
        commonMistakes: 'Common mistakes to avoid',
        checklist: 'Self-check',
        nextLesson: 'Next lesson',
        markCompleted: 'Mark as completed',
        assignedExercises: 'Practice these exercises',
        assignedSongs: 'Apply it on these songs',
        relatedLesson: 'Related lesson',
        loadingNotation: 'Loading notation...',
        loadingAudio: 'Loading audio...',
        playAudio: 'Play audio example',
        invalidNotation: 'Invalid notation',
        invalidAudio: 'Invalid audio example',
        expandStaff: 'Expand fullscreen'
      },
      // Songs
      songs: {
        title: 'Song Library',
        bpm: 'BPM',
        chordsUsed: 'Chords used',
        difficulty: 'Difficulty',
        searchPlaceholder: 'Search by name...',
        emptyLibrary: 'No songs in your library',
        noResults: 'No results',
        saved: 'Song saved to library',
        saveError: 'Failed to save',
        removed: 'Song removed from library',
        removeError: 'Failed to remove',
        presetSaved: 'Preset saved',
        presetSaveError: 'Failed to save preset',
        dbUnavailable: 'Local library unavailable. This browser may not support IndexedDB.',
        unsupportedFormat: 'Format not supported by the browser',
        limitReached: 'Limit reached ({{count}} songs). Upgrade to PRO for unlimited.',
        loopOn: 'Loop ON',
        loopOff: 'Loop OFF',
        loaded: 'Loaded',
        load: 'Load',
        statusPlaying: 'Playing',
        statusError: 'Error',
        statusReady: 'Ready',
        timelineAria: 'Multitrack timeline',
        loopActive: 'Loop on',
        loopInactive: 'Loop off',
        mtLimitPro: 'Max {{max}} tracks. Remove one to add more.',
        mtLimitFree: 'Max {{free}} tracks. Upgrade to PRO for {{pro}}.',
        mtUploadedSome: 'Loaded {{loaded}} of {{total}} tracks (limit: {{limit}})',
        mtInvalidTrack: 'No valid track',
        mtSeekOutOfRange: 'Position past the end of all tracks',
        practiceLogin: 'Sign in to save your progress',
        practiced: 'Song practiced! 🎸',
        practiceError: 'Failed to save',
        autoScroll: 'Auto-scroll',
        autoScrollShort: 'Auto',
        scrollSpeed: 'Speed',
        metronomeOn: 'Metronome on',
        metronomeOff: 'Metronome off',
        metronome: 'Click',
        drumsOn: 'Drums on',
        drumsOff: 'Drums off',
        drums: 'Drums',
        viewChords: 'Chords',
        viewTab: 'Tablature',
        tabIntro: 'Polyphonic arrangement for classical/fingerstyle guitar — melody and bass notated together. Read top-down: each string of the tab matches the corresponding string of the guitar (top = high E).',
        tabNotation: 'Tablature',
        noTabAvailable: 'No tablature has been written for this song yet.',
        noTabFallback: 'Switch back to "Chords" for the chord-chart view of the verses.',
        backingBand: 'Backing band',
        stemDrums: 'Drums',
        stemBass: 'Bass',
        stemPiano: 'Piano',
        stemOther: 'Melody',
        stemVocals: 'Vocals',
        stemFullMix: 'Full mix',
        audioBacking: 'Real audio backing',
        noAudioBacking: 'No real audio backing yet for this song.',
        audioBackingError: 'No audio stem available for this song.',
        audioStemsHidden: '{{n}} stems hidden (empty or unavailable)',
        audioStemEmpty: 'silent or empty file',
        audioStemFailed: 'failed to load',
        audioSetA: 'Set loop start',
        audioSetB: 'Set loop end',
        audioClearLoop: 'Clear loop',
        audioSpeed: 'Speed'
      },
      // Tuner
      tuner: {
        title: 'Chromatic Tuner',
        note: 'Note',
        frequency: 'Frequency',
        cents: 'Cents',
        inTune: 'In Tune!',
        tooLow: 'Too Low',
        tooHigh: 'Too High',
        start: 'Start Tuner',
        stop: 'Stop Tuner',
        standardTuning: 'Standard Tuning: E A D G B E',
        reference: 'Standard tuning reference',
        string: 'String'
      },
      // Recorder
      recorder: {
        title: 'Audio Recorder',
        tagline: 'Looper & multitrack',
        subtitle: 'Capture an idea, layer up to five tracks, save sessions on this device. Overdub, mixer, A/B loop and WAV export are PRO.',
        record: 'Record',
        stop: 'Stop',
        play: 'Play',
        pause: 'Pause',
        save: 'Save Recording',
        delete: 'Delete',
        recordings: 'My Recordings',
        noRecordings: 'No recordings yet',
        duration: 'Duration',
        micEnabled: 'Microphone enabled!',
        startError: 'Failed to start recording',
        freeLimit: 'Free limit reached (3 recordings). Upgrade to PRO!',
        recordingSaved: 'Recording saved!',
        recordingDeleted: 'Recording deleted',
        deleteError: 'Failed to delete',
        metadataSaved: 'Metadata saved!',
        saveError: 'Failed to save',
        wavProOnly: 'WAV download is PRO-only',
        downloadStarted: 'Download started!',
        downloadError: 'Download failed',
        audioMissing: 'Audio not available for this recording',
        audioEmpty: 'Empty audio: recording was not saved correctly',
        playError: 'Audio playback error',
        notFound: 'Recording not found',
        loopABeforeB: 'Point A must come before point B',
        setAFirst: 'Set point A first',
        loopBAfterA: 'Point B must come after point A',
        audioStopped: 'Audio stopped',
        loopRequiredForOverdub: 'Enable a loop before overdubbing',
        micRequired: 'Microphone required for overdub',
        micStreamInactive: 'Microphone stream inactive',
        overdubStarted: 'Overdub recording!',
        overdubStartError: 'Failed to start overdub',
        overdubCancelled: 'Overdub cancelled',
        noAudioRecorded: 'No audio recorded',
        overdubSaved: 'Overdub saved!',
        overdubSaveError: 'Failed to save overdub',
        overdubProcessError: 'Failed to process overdub',
        overdubRemoved: 'Overdub removed',
        overdubRemoveError: 'Failed to remove overdub',
        sessionNameRequired: 'Enter a session name',
        noTrackToSave: 'No track selected to save',
        noValidAudioToSave: 'No valid audio to save',
        sessionSaved: 'Session saved!',
        sessionSaveError: 'Failed to save session',
        sessionNotFound: 'Session not found',
        sessionInvalidTracks: 'No valid track in the session',
        sessionLoadError: 'Failed to load session',
        sessionDeleted: 'Session deleted',
        sessionDeleteError: 'Failed to delete session',
        micErrorGeneric: 'Microphone access error',
        micErrorPermission: 'Microphone permission denied. Allow it in the browser and reload.',
        micErrorNotFound: 'No microphone detected.',
        micErrorBusy: 'Microphone busy in another application.',
        micErrorConstraints: 'Microphone configuration not supported.',
        loadAudioError: 'Failed to load audio: {{message}}',
        unknownError: 'Unknown error',
        multitrackFormatError: 'Unsupported format on one or more tracks',
        multitrackError: 'Multitrack error: {{message}}',
        startPrompt: 'Click to enable the microphone and start recording',
        recordingNamePlaceholder: 'Recording name...',
        stopAllTitle: 'Stop everything immediately',
        mute: 'Mute',
        unmute: 'Unmute',
        solo: 'Solo',
        unsolo: 'Unsolo',
        hide: 'Hide',
        show: 'Show',
        sessionNamePlaceholder: 'Session name...',
        editTitle: 'Edit',
        downloadWav: 'Download WAV',
        proOnly: 'PRO only',
        notesPlaceholder: 'Add notes about this recording...',
        tagsPlaceholder: 'Add tag...'
      },
      // Drums
      drums: {
        title: 'Drum Tracks',
        tagline: 'Drum tracks',
        subtitle: 'Drum loops ready to practice over. Filter by genre, time signature or difficulty. Every BPM is adjustable in real time.',
        genres: {
          rock: 'Rock',
          pop: 'Pop',
          blues: 'Blues',
          jazz: 'Jazz',
          metal: 'Metal',
          funk: 'Funk'
        },
        bpm: 'BPM',
        play: 'Play',
        stop: 'Stop',
        tempo: 'Tempo',
        availableCount: '{{count}} tracks available',
        genre: 'Genre',
        level: 'Level',
        allGenres: 'All genres',
        allLevels: 'All levels',
        noResults: 'No tracks found',
        style: 'Style',
        volume: 'Vol'
      },
      // Metronome
      metronome: {
        title: 'Metronome',
        subtitle: 'Professional timing with Web Audio',
        start: 'Start',
        stop: 'Stop',
        tapTempo: 'Tap Tempo',
        settings: 'Settings',
        volume: 'Volume',
        subdivision: 'Subdivision',
        timeSignature: 'Time signature',
        proOnly: 'PRO only'
      },
      // Tutor
      tutor: {
        title: 'Maestro — AI guitar tutor',
        subtitle: 'Ask anything, get a tailored answer.',
        welcome: 'Hi, I am Maestro.',
        welcomeSubtitle: 'Tell me what you would like to work on today, or pick one of the prompts below.',
        newChat: 'New chat',
        noHistory: 'No previous chats yet.',
        untitled: 'Untitled chat',
        placeholder: 'Ask Maestro anything about guitar...',
        disclaimer: 'Maestro is an AI assistant and can make mistakes. Double-check critical advice.',
        loginRequired: 'Sign in to chat with Maestro.',
        loadError: 'Failed to load the conversation.',
        sendError: 'Failed to send. Try again.',
        notConfigured: 'Tutor is not configured on the server.',
        deleteConfirm: 'Delete this conversation?',
        deleted: 'Conversation deleted.',
        deleteError: 'Failed to delete the conversation.'
      },
      // Premium
      premium: {
        title: 'Go Premium',
        subtitle: 'Unlock all features and take your playing to the next level',
        price: '€4.99',
        oneTime: 'One-time payment',
        features: {
          title: "What's included with Premium",
          advancedChords: 'Advanced chord voicings',
          expertExercises: 'Expert-level exercises',
          fullTheory: 'Complete theory course',
          allSongs: 'Full song library',
          drumCustomization: 'Custom drum patterns',
          unlimitedRecordings: 'Unlimited recordings'
        },
        cta: 'Upgrade Now',
        alreadyPremium: 'You are a Premium member!',
        thanks: 'Thanks for your support! Enjoy all premium features.',
        planLabel: 'Plan',
        bestValue: 'BEST VALUE',
        notLoggedIn: 'Sign in to activate Premium',
        notLoggedInCta: 'Sign in / Sign up',
        periods: {
          month: '/month',
          year: '/year',
          lifetime: 'one-time'
        },
        trust: {
          instantAccess: 'Instant Access',
          allContent: 'All Content',
          futureUpdates: 'Future Updates'
        },
        checkout: {
          proceed: 'Continue to checkout',
          redirecting: 'Redirecting to payment...',
          securedBy: 'Secure payment by Stripe',
          cancelled: 'Payment cancelled',
          activated: 'Premium activated! 🎉',
          processing: 'Payment is processing, reload in a moment.',
          errorPlans: 'Failed to load plans',
          errorAuth: 'Session expired. Please sign in again.',
          errorStart: 'Failed to start payment',
          errorOpen: 'Could not start checkout'
        }
      },
      // Profile
      profile: {
        language: 'Language',
        accountStatus: 'Account Status',
        level: 'Level'
      },
      // Dashboard
      dashboard: {
        welcome: 'Welcome back',
        continueLeaning: 'Continue Learning',
        quickAccess: 'Quick Access',
        yourLevel: 'Your Level',
        recentActivity: 'Recent Activity',
        startToday: "Start today's practice",
        startTodayDesc: 'Pick up where you left off and keep improving your guitar skills.',
        status: 'Status',
        upgrade: 'Upgrade',
        upgradeProCta: 'Upgrade to PRO',
        yourProgress: 'Your progress',
        total: 'Total',
        advancedStats: 'Advanced stats',
        activeDays: 'Active days',
        totalTime: 'Total time',
        last7Days: 'Last 7 days',
        proOnly: 'Advanced stats are PRO-only',
        metronomeDesc: 'Precise timing',
        recorderDesc: 'Record and save',
        units: {
          chords: 'chords',
          exercises: 'exercises',
          lessons: 'lessons',
          songs: 'songs',
          tracks: 'tracks'
        }
      }
    }
  },
  it: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        chords: 'Accordi',
        exercises: 'Esercizi',
        etudes: 'Studi',
        theory: 'Teoria',
        songs: 'Brani',
        tuner: 'Accordatore',
        recorder: 'Registratore',
        drums: 'Basi Batteria',
        metronome: 'Metronomo',
        tutor: 'Maestro AI',
        skills: 'Skill Tree',
        progress: 'Progressi',
        premium: 'Premium',
        login: 'Accedi',
        register: 'Registrati',
        logout: 'Esci',
        dashboard: 'Dashboard',
        profile: 'Profilo'
      },
      // Landing Page
      landing: {
        hero: {
          tagline: 'L\'app per chitarristi adulti — nata in Italia, bilingue per design',
          title: 'Impara la chitarra come si deve.',
          subtitle: 'Dai primi accordi al fingerstyle, con tablature polifoniche scritte a mano e un maestro AI sempre a disposizione. Italiano e inglese veri, non traduzioni automatiche. Niente video infiniti, niente gamification puerile.',
          cta: 'Inizia Gratis',
          ctaLogin: 'Accedi'
        },
        features: {
          title: 'Cosa ti dà FretFlow',
          chords: {
            title: 'Libreria Accordi',
            desc: '209 accordi con diagrammi, dita, varianti e barré'
          },
          exercises: {
            title: 'Esercizi Tecnici',
            desc: 'Spider, scale, picking, sweep — tutti col metronomo integrato'
          },
          theory: {
            title: 'Teoria Musicale',
            desc: '54 lezioni dal pentagramma alla riarmonizzazione, in italiano'
          },
          tuner: {
            title: 'Accordatore Cromatico',
            desc: 'Accordatura in tempo reale dal microfono'
          },
          recorder: {
            title: 'Registratore Multitrack',
            desc: 'Registra la chitarra, sovrapponi voce e basso'
          },
          drums: {
            title: 'Backing & Drum Tracks',
            desc: 'Basi di batteria + base band sintetica + audio reali sui brani PD'
          }
        },
        differentiators: {
          title: 'Perché FretFlow è diverso',
          italian: {
            title: 'Bilingue, scritta a mano',
            desc: 'Italiano e inglese veri, scritti da chitarristi non da Google Translate'
          },
          fingerstyle: {
            title: 'Fingerstyle e classica',
            desc: '30 tablature polifoniche scritte a mano (melodia + basso intrecciati)'
          },
          maestro: {
            title: 'Maestro AI',
            desc: 'Il tuo insegnante personale, risponde alle tue domande di chitarra 24/7'
          },
          allInOne: {
            title: 'Tutto in un\'app',
            desc: 'Tuner, metronomo, accordi, lezioni, brani, recorder — niente 5 app da gestire'
          }
        },
        levels: {
          title: 'Per Ogni Livello',
          beginner: 'Principiante',
          intermediate: 'Intermedio',
          expert: 'Avanzato',
          beginnerDesc: 'Dai primi accordi alle prime canzoni',
          intermediateDesc: 'Scale, teoria, fingerstyle, stili',
          expertDesc: 'Modi, riarmonizzazione, improvvisazione'
        },
        finalCta: {
          title: 'Pronto a fare sul serio?',
          subtitle: 'Tier gratuito vero, niente trial-castrato. Premium €5,99/mese o €49,99/anno quando vuoi sbloccare tutto.'
        },
        footer: {
          rights: 'Tutti i diritti riservati.'
        }
      },
      progress: {
        tagline: 'Practice DNA',
        title: 'Come stai evolvendo',
        subtitle: 'Copertura degli skill per categoria, progressione BPM sugli esercizi tecnici, streak a 90 giorni e i brani in repertorio.',
        loginRequired: 'Accedi per vedere i tuoi progressi.',
        streakNow: 'Streak attuale',
        streakLongest: 'Streak più lunga',
        lessonsDone: 'Lezioni',
        songsPlayed: 'Brani suonati',
        cards: {
          radarTitle: 'Copertura skill',
          radarSubtitle: 'Percentuale di skill padroneggiati per categoria',
          radarTooltip: 'padroneggiati',
          bpmTitle: 'Progressione BPM',
          bpmSubtitle: 'BPM max + medio raggiunti sugli esercizi (ultimi 90 giorni)',
          bpmEmpty: 'Nessun BPM registrato. Apri un esercizio e salva il BPM raggiunto.',
          bpmMax: 'Max',
          bpmAvg: 'Medio',
          heatmapTitle: 'Streak delle sessioni quotidiane',
          heatmapSubtitle: 'Ogni quadrato = un giorno. Oro = sessione di oggi completata.',
          heatmapDays: 'giorni',
          heatmapLegendLess: 'Saltato',
          heatmapLegendMore: 'Fatto',
          repertoireTitle: 'Il tuo repertorio',
          repertoireSubtitle: 'Brani avviati o completati',
          repertoireEmpty: 'Apri un brano per iniziare il tuo repertorio.',
          openLibrary: 'Libreria',
          repertoireStatus: {
            in_progress: 'In studio',
            completed: 'Padroneggiato',
            not_started: 'Non iniziato'
          }
        }
      },
      skills: {
        tagline: 'Il tuo skill tree',
        title: 'Dove stai sulla chitarra',
        subtitle: 'Una mappa delle abilità chitarristiche, dal primo accordo all\'armonia avanzata. Ogni lezione, esercizio e brano che completi sblocca i nodi successivi.',
        empty: 'Nessuno skill corrisponde a questo filtro.',
        prerequisites: 'Prerequisiti',
        howToMaster: 'Completa questo skill con',
        skillsLabel: 'skill',
        filters: {
          all: 'Tutti',
          available: 'Disponibili',
          mastered: 'Padroneggiati',
          locked: 'Bloccati',
          allCategories: 'Tutte le categorie'
        },
        states: {
          mastered: 'Padroneggiato',
          available: 'Disponibile',
          locked: 'Bloccato'
        },
        stats: {
          mastered: 'Padroneggiati',
          available: 'Disponibili ora',
          locked: 'Ancora bloccati'
        },
        difficulty: {
          beginner: 'Principiante',
          intermediate: 'Intermedio',
          advanced: 'Avanzato'
        },
        categories: {
          fundamentals: 'Fondamentali',
          chords: 'Accordi',
          rhythm: 'Ritmo',
          technique: 'Tecnica',
          scales: 'Scale',
          theory: 'Teoria',
          style: 'Stili',
          repertoire: 'Repertorio'
        },
        types: {
          lesson: 'Lezione',
          exercise: 'Esercizio',
          song: 'Brano'
        }
      },
      session: {
        todayLabel: 'Sessione di oggi',
        minutes: 'minuti',
        minShort: 'min',
        done: 'fatti',
        open: 'Apri',
        start: 'Inizia',
        freeplay: 'Suona libero',
        markDone: 'Marca fatto',
        streakLabel: 'giorni di fila',
        completedTitle: 'Sessione completata!',
        completedDesc: 'Streak a {{n}} giorni — torna domani per non spezzarla.',
        types: {
          warmup: 'Riscaldamento',
          lesson: 'Lezione',
          song: 'Brano',
          freeplay: 'Free play'
        }
      },
      onboarding: {
        title: 'Benvenuto',
        step: 'Passo {{current}} di {{total}}',
        back: 'Indietro',
        next: 'Avanti',
        finish: 'Vai alla mia dashboard',
        skip: 'Salta per ora',
        levelTitle: 'A che livello sei?',
        levelSubtitle: 'Calibriamo la sessione di oggi su questo. Nessuna risposta sbagliata.',
        goalTitle: 'Cosa vuoi ottenere?',
        goalSubtitle: 'Scegli quello che ti rappresenta oggi. Puoi cambiarlo dopo.',
        genresTitle: 'Quali generi ti tirano dentro?',
        genresSubtitle: 'Selezionane almeno uno. I brani consigliati seguiranno questa scelta.',
        chooseAtLeastOne: 'Seleziona almeno un genere per continuare',
        savingError: 'Non sono riuscito a salvare le preferenze. Riprova fra un momento.',
        levels: {
          beginner: { label: 'Principiante', desc: 'Non ho mai suonato, o appena' },
          some_chords: { label: 'So qualche accordo', desc: 'Strummo Do, Sol, Re, Lam — non sempre puliti' },
          intermediate: { label: 'Intermedio', desc: 'Barré, scale, qualche brano completo' },
          advanced: { label: 'Avanzato', desc: 'Modi, fingerstyle, improvvisazione' }
        },
        goals: {
          play_songs: { label: 'Cantare i miei brani preferiti', desc: 'Accordi, strumming, accompagnamento al canto' },
          fingerstyle: { label: 'Suonare fingerstyle e classica', desc: 'Melodia + basso su una sola chitarra' },
          improvise: { label: 'Improvvisare assoli', desc: 'Scale, vocabolario blues, rock, jazz' },
          theory: { label: 'Capire la musica', desc: 'Teoria, armonia, ear training' },
          mix: { label: 'Un po\' di tutto', desc: 'Percorso ampio senza un focus fisso' }
        },
        genres: {
          pop: 'Pop / Rock',
          folk: 'Folk',
          fingerstyle: 'Fingerstyle',
          blues: 'Blues',
          jazz: 'Jazz',
          classical: 'Classica'
        }
      },
      // Common
      common: {
        loading: 'Caricamento...',
        error: 'Errore',
        success: 'Successo',
        save: 'Salva',
        cancel: 'Annulla',
        delete: 'Elimina',
        edit: 'Modifica',
        back: 'Indietro',
        next: 'Avanti',
        previous: 'Precedente',
        search: 'Cerca',
        filter: 'Filtra',
        refresh: 'Aggiorna',
        all: 'Tutti',
        free: 'Gratuito',
        premium: 'Premium',
        locked: 'Solo Premium',
        unlock: 'Sblocca con Premium'
      },
      // Levels
      levels: {
        beginner: 'Principiante',
        intermediate: 'Intermedio',
        expert: 'Esperto',
        advanced: 'Avanzato',
        master: 'Maestro'
      },
      // Auth
      auth: {
        email: 'Email',
        password: 'Password',
        username: 'Nome utente',
        login: 'Accedi',
        register: 'Crea Account',
        forgotPassword: 'Password dimenticata?',
        noAccount: 'Non hai un account?',
        hasAccount: 'Hai già un account?',
        loginSuccess: 'Bentornato!',
        registerSuccess: 'Account creato con successo!',
        failed: 'Autenticazione fallita',
        warmupTitle: 'Il server si sta svegliando',
        warmupDesc: 'La prima volta del giorno il tier gratuito di Render impiega 30-40 secondi a partire. Le volte successive sarà istantaneo.'
      },
      // Chords
      chords: {
        title: 'Libreria Accordi',
        tagline: 'Libreria accordi',
        subtitle: 'Diagrammi, dita, varianti e barré per 209 accordi. Click su un accordo per sentirlo con campioni di chitarra acustica reale.',
        realisticToggleTitle: 'Chitarra acustica reale vs synth',
        realisticOn: 'Reale',
        realisticOff: 'Synth',
        realisticLoading: 'Carico...',
        barre: 'Barré',
        listen: 'Ascolta',
        theoryNote: 'Nota di teoria',
        fretBoardLayout: 'Corde (6 → 1)',
        fingers: 'Dita',
        actionPlay: 'Suona',
        actionStrumDown: 'Strum ↓',
        actionStrumUp: 'Strum ↑',
        theoryNotes: {
          major: 'Triade maggiore: tonica + 3a maggiore + 5a giusta. Suono brillante, "allegro". L\'ossatura di pop e folk.',
          minor: 'Triade minore: tonica + 3a minore + 5a giusta. Carattere più scuro, malinconico. Si trova ovunque, dal blues al classico.',
          '7th': 'Dominante 7: triade + 7a minore. Vuole risolvere — guida la cadenza V → I al cuore della musica tonale.',
          maj7: 'Settima maggiore: triade + 7a maggiore. Suono jazz, dolce, sognante. Steely Dan e bossa nova.',
          m7: 'Minore 7: triade minore + 7a minore. Usato come ii nelle ii-V-I jazz e nei vamp minori caldi.',
          sus: 'Sospeso: la 3a è sostituita da una 2a o una 4a. Crea tensione che vuole tornare all\'accordo maggiore.',
          dim: 'Diminuito: triade minore con 5a abbassata. Inquieto, di passaggio — quasi sempre transita verso un altro accordo.',
          aug: 'Aumentato: triade maggiore con 5a alzata. Simmetrico e instabile — divide l\'ottava in terze maggiori uguali.',
          add: 'Accordo "add": l\'estensione indicata viene aggiunta senza la 7a. L\'add9 resta aperto e arioso.',
          power: 'Power chord: tonica + 5a giusta, niente 3a. Né maggiore né minore — il cardine di rock e metal.'
        },
        categories: {
          major: 'Maggiore',
          minor: 'Minore',
          '7th': 'Settima',
          maj7: 'Maggiore 7',
          m7: 'Minore 7',
          sus: 'Sospeso',
          dim: 'Diminuito',
          aug: 'Aumentato'
        },
        strings: 'Corde',
        frets: 'Tasti',
        finger: 'Dito',
        availableCount: '{{count}} accordi disponibili',
        searchPlaceholder: 'Cerca accordi...',
        difficulty: 'Difficoltà',
        rootNote: 'Nota',
        category: 'Categoria',
        pageInfo: 'Pagina {{page}} di {{total}}',
        arpeggio: 'Arpeggio',
        proOnly: 'Solo PRO'
      },
      // Exercises
      exercises: {
        title: 'Esercizi Tecnici',
        categories: {
          technique: 'Tecnica',
          speed: 'Velocità',
          fingerpicking: 'Fingerpicking',
          chords: 'Cambi Accordi'
        },
        duration: '{{minutes}} min',
        start: 'Inizia Esercizio',
        difficulty: 'Difficoltà',
        category: 'Categoria',
        tabNotation: 'Notazione tab:',
        instructions: 'Istruzioni passo-passo',
        tips: 'Consigli',
        bpmStart: 'BPM iniziale',
        bpmTarget: 'BPM obiettivo',
        noResults: 'Nessun esercizio corrisponde ai filtri',
        completed: 'Esercizio completato! ✓',
        saveLogin: 'Accedi per salvare i progressi',
        saveError: 'Errore nel salvataggio',
        relatedLesson: 'Lezione collegata'
      },
      // Theory
      theory: {
        title: 'Lezioni di Teoria Musicale',
        categories: {
          basics: 'Basi',
          fundamentals: 'Fondamentali',
          scales: 'Scale',
          harmony: 'Armonia',
          modes: 'Modi',
          rhythm: 'Ritmo',
          chords: 'Accordi',
          technique: 'Tecnica',
          theory: 'Tema teorico',
          style: 'Stile',
          history: 'Storia',
          posture: 'Postura',
          improvisation: 'Improvvisazione',
          composition: 'Composizione'
        },
        subjects: {
          history: 'Storia',
          theory: 'Teoria musicale',
          technique: 'Tecnica',
          style: 'Stili e repertorio',
          gear: 'Strumento e gear',
          practice: 'Pratica e metodo'
        },
        difficulty: 'Difficoltà',
        subject: 'Materia',
        category: 'Categoria',
        noResults: 'Nessuna lezione corrisponde ai filtri',
        completed: 'Lezione completata! ✓',
        saveLogin: 'Accedi per salvare i progressi',
        saveError: 'Errore nel salvataggio',
        duration: '{{minutes}} min',
        objectives: 'Obiettivi della lezione',
        commonMistakes: 'Errori comuni da evitare',
        checklist: 'Verifica di aver capito',
        nextLesson: 'Prossima lezione',
        markCompleted: 'Segna come completata',
        assignedExercises: 'Esercitati con questi',
        assignedSongs: 'Applicalo su questi brani',
        relatedLesson: 'Lezione collegata',
        loadingNotation: 'Caricamento notazione...',
        loadingAudio: 'Caricamento audio...',
        playAudio: 'Riproduci esempio sonoro',
        invalidNotation: 'Notazione non valida',
        invalidAudio: 'Audio example invalido',
        expandStaff: 'Espandi a tutto schermo'
      },
      // Songs
      songs: {
        title: 'Libreria Brani',
        bpm: 'BPM',
        chordsUsed: 'Accordi usati',
        difficulty: 'Difficoltà',
        searchPlaceholder: 'Cerca per nome...',
        emptyLibrary: 'Nessun brano in libreria',
        noResults: 'Nessun risultato',
        saved: 'Brano salvato in libreria',
        saveError: 'Errore nel salvataggio',
        removed: 'Brano rimosso dalla libreria',
        removeError: 'Errore nella rimozione',
        presetSaved: 'Preset salvato',
        presetSaveError: 'Errore nel salvataggio preset',
        dbUnavailable: 'Libreria locale non disponibile. Il browser potrebbe non supportare IndexedDB.',
        unsupportedFormat: 'Formato non supportato dal browser',
        limitReached: 'Limite raggiunto ({{count}} brani). Passa a PRO per illimitati.',
        loopOn: 'Loop ON',
        loopOff: 'Loop OFF',
        loaded: 'Caricato',
        load: 'Carica',
        statusPlaying: 'In riproduzione',
        statusError: 'Errore',
        statusReady: 'Pronto',
        timelineAria: 'Timeline multitrack',
        loopActive: 'Loop attivo',
        loopInactive: 'Loop disattivato',
        mtLimitPro: 'Massimo {{max}} tracce. Rimuovi una traccia per aggiungerne altre.',
        mtLimitFree: 'Massimo {{free}} tracce. Passa a PRO per {{pro}}.',
        mtUploadedSome: 'Caricate {{loaded}} di {{total}} tracce (limite: {{limit}})',
        mtInvalidTrack: 'Nessuna traccia valida',
        mtSeekOutOfRange: 'Posizione oltre la fine delle tracce',
        practiceLogin: 'Accedi per salvare i progressi',
        practiced: 'Brano praticato! 🎸',
        practiceError: 'Errore nel salvataggio',
        autoScroll: 'Scorrimento auto',
        autoScrollShort: 'Auto',
        scrollSpeed: 'Velocità',
        metronomeOn: 'Metronomo attivo',
        metronomeOff: 'Metronomo spento',
        metronome: 'Click',
        drumsOn: 'Batteria attiva',
        drumsOff: 'Batteria spenta',
        drums: 'Batteria',
        viewChords: 'Accordi',
        viewTab: 'Tablatura',
        tabIntro: 'Arrangiamento polifonico per chitarra classica / fingerstyle — melodia e basso scritti insieme. Si legge dall\'alto in basso: ogni riga della tab corrisponde a una corda della chitarra (in alto = Mi cantino).',
        tabNotation: 'Tablatura',
        noTabAvailable: 'Nessuna tablatura disponibile per questo brano.',
        noTabFallback: 'Passa alla vista "Accordi" per il chord chart sopra il testo.',
        backingBand: 'Base band',
        stemDrums: 'Batteria',
        stemBass: 'Basso',
        stemPiano: 'Pianoforte',
        stemOther: 'Melodia',
        stemVocals: 'Voce',
        stemFullMix: 'Mix completo',
        audioBacking: 'Base audio reale',
        noAudioBacking: 'Nessuna base audio reale per questo brano.',
        audioBackingError: 'Nessuno stem audio disponibile per questo brano.',
        audioStemsHidden: '{{n}} stem nascosti (vuoti o non disponibili)',
        audioStemEmpty: 'silenzio o file vuoto',
        audioStemFailed: 'caricamento fallito',
        audioSetA: 'Inizio loop',
        audioSetB: 'Fine loop',
        audioClearLoop: 'Annulla loop',
        audioSpeed: 'Velocità'
      },
      // Tuner
      tuner: {
        title: 'Accordatore Cromatico',
        note: 'Nota',
        frequency: 'Frequenza',
        cents: 'Centesimi',
        inTune: 'Accordato!',
        tooLow: 'Troppo Basso',
        tooHigh: 'Troppo Alto',
        start: 'Avvia Accordatore',
        stop: 'Ferma Accordatore',
        standardTuning: 'Accordatura Standard: Mi La Re Sol Si Mi',
        reference: 'Riferimento accordatura standard',
        string: 'Corda'
      },
      // Recorder
      recorder: {
        title: 'Registratore Audio',
        tagline: 'Looper e multitraccia',
        subtitle: 'Cattura un\'idea, sovrapponi fino a cinque tracce, salva sessioni su questo dispositivo. Overdub, mixer, loop A/B ed export WAV sono PRO.',
        record: 'Registra',
        stop: 'Stop',
        play: 'Riproduci',
        pause: 'Pausa',
        save: 'Salva Registrazione',
        delete: 'Elimina',
        recordings: 'Le Mie Registrazioni',
        noRecordings: 'Nessuna registrazione',
        duration: 'Durata',
        micEnabled: 'Microfono attivato!',
        startError: 'Errore avvio registrazione',
        freeLimit: 'Limite Free raggiunto (3 registrazioni). Passa a PRO!',
        recordingSaved: 'Registrazione salvata!',
        recordingDeleted: 'Registrazione eliminata',
        deleteError: "Errore nell'eliminazione",
        metadataSaved: 'Metadata salvati!',
        saveError: 'Errore nel salvataggio',
        wavProOnly: 'Download WAV disponibile solo per utenti PRO',
        downloadStarted: 'Download avviato!',
        downloadError: 'Errore nel download',
        audioMissing: 'Audio non disponibile per questa registrazione',
        audioEmpty: 'Audio vuoto: registrazione non salvata correttamente',
        playError: 'Errore riproduzione audio',
        notFound: 'Registrazione non trovata',
        loopABeforeB: 'Il punto A deve essere prima del punto B',
        setAFirst: 'Imposta prima il punto A',
        loopBAfterA: 'Il punto B deve essere dopo il punto A',
        audioStopped: 'Audio fermato',
        loopRequiredForOverdub: 'Attiva un loop prima di fare overdub',
        micRequired: 'Microfono richiesto per overdub',
        micStreamInactive: 'Stream microfono non attivo',
        overdubStarted: 'Overdub in registrazione!',
        overdubStartError: 'Errore avvio overdub',
        overdubCancelled: 'Overdub annullato',
        noAudioRecorded: 'Nessun audio registrato',
        overdubSaved: 'Overdub salvato!',
        overdubSaveError: 'Errore salvataggio overdub',
        overdubProcessError: 'Errore elaborazione overdub',
        overdubRemoved: 'Overdub rimosso',
        overdubRemoveError: 'Errore rimozione overdub',
        sessionNameRequired: 'Inserisci un nome per la sessione',
        noTrackToSave: 'Nessuna traccia selezionata da salvare',
        noValidAudioToSave: 'Nessun audio valido da salvare',
        sessionSaved: 'Sessione salvata!',
        sessionSaveError: 'Errore salvataggio sessione',
        sessionNotFound: 'Sessione non trovata',
        sessionInvalidTracks: 'Nessuna traccia valida nella sessione',
        sessionLoadError: 'Errore caricamento sessione',
        sessionDeleted: 'Sessione eliminata',
        sessionDeleteError: 'Errore eliminazione sessione',
        micErrorGeneric: 'Errore accesso microfono',
        micErrorPermission: 'Permesso microfono negato. Consenti dal browser e ricarica la pagina.',
        micErrorNotFound: 'Nessun microfono trovato.',
        micErrorBusy: "Microfono già in uso da un'altra applicazione.",
        micErrorConstraints: 'Configurazione microfono non supportata.',
        loadAudioError: 'Errore caricamento audio: {{message}}',
        unknownError: 'Errore sconosciuto',
        multitrackFormatError: 'Formato non supportato per una o più tracce',
        multitrackError: 'Errore multitrack: {{message}}',
        startPrompt: 'Clicca per abilitare il microfono e iniziare a registrare',
        recordingNamePlaceholder: 'Nome registrazione...',
        stopAllTitle: 'Ferma tutto immediatamente',
        mute: 'Mute',
        unmute: 'Unmute',
        solo: 'Solo',
        unsolo: 'Unsolo',
        hide: 'Nascondi',
        show: 'Mostra',
        sessionNamePlaceholder: 'Nome sessione...',
        editTitle: 'Modifica',
        downloadWav: 'Download WAV',
        proOnly: 'Solo PRO',
        notesPlaceholder: 'Aggiungi note sulla registrazione...',
        tagsPlaceholder: 'Aggiungi tag...'
      },
      // Drums
      drums: {
        title: 'Basi di Batteria',
        tagline: 'Basi di batteria',
        subtitle: 'Loop di batteria pronti per esercitarti sopra. Filtra per genere, tempo o difficoltà. Tutti i BPM regolabili in tempo reale.',
        genres: {
          rock: 'Rock',
          pop: 'Pop',
          blues: 'Blues',
          jazz: 'Jazz',
          metal: 'Metal',
          funk: 'Funk'
        },
        bpm: 'BPM',
        play: 'Riproduci',
        stop: 'Stop',
        tempo: 'Tempo',
        availableCount: '{{count}} basi disponibili',
        genre: 'Genere',
        level: 'Livello',
        allGenres: 'Tutti i generi',
        allLevels: 'Tutti i livelli',
        noResults: 'Nessuna base trovata',
        style: 'Stile',
        volume: 'Vol'
      },
      // Metronome
      metronome: {
        title: 'Metronomo',
        subtitle: 'Timing professionale con Web Audio',
        start: 'Start',
        stop: 'Stop',
        tapTempo: 'Tap Tempo',
        settings: 'Impostazioni',
        volume: 'Volume',
        subdivision: 'Sottodivisione',
        timeSignature: 'Tempo',
        proOnly: 'Solo PRO'
      },
      // Tutor
      tutor: {
        title: 'Maestro — tutor di chitarra AI',
        subtitle: 'Chiedi quello che vuoi, risposta su misura.',
        welcome: 'Ciao, sono Maestro.',
        welcomeSubtitle: 'Dimmi su cosa vorresti lavorare oggi, oppure scegli uno dei suggerimenti qui sotto.',
        newChat: 'Nuova chat',
        noHistory: 'Nessuna chat precedente.',
        untitled: 'Chat senza titolo',
        placeholder: 'Chiedi a Maestro qualsiasi cosa sulla chitarra...',
        disclaimer: 'Maestro è un assistente AI e può sbagliare. Verifica i consigli critici.',
        loginRequired: 'Accedi per chattare con Maestro.',
        loadError: 'Errore nel caricamento della conversazione.',
        sendError: 'Errore di invio. Riprova.',
        notConfigured: 'Tutor non configurato sul server.',
        deleteConfirm: 'Eliminare questa conversazione?',
        deleted: 'Conversazione eliminata.',
        deleteError: 'Errore nell\'eliminazione della conversazione.'
      },
      // Premium
      premium: {
        title: 'Diventa Premium',
        subtitle: 'Sblocca tutte le funzionalità e porta la tua chitarra al livello successivo',
        price: '€4,99',
        oneTime: 'Pagamento unico',
        features: {
          title: 'Cosa include Premium',
          advancedChords: 'Accordi avanzati',
          expertExercises: 'Esercizi livello esperto',
          fullTheory: 'Corso di teoria completo',
          allSongs: 'Libreria brani completa',
          drumCustomization: 'Pattern batteria personalizzati',
          unlimitedRecordings: 'Registrazioni illimitate'
        },
        cta: 'Acquista Ora',
        alreadyPremium: 'Sei già Premium!',
        thanks: 'Grazie per il tuo supporto! Goditi tutte le funzionalità premium.',
        planLabel: 'Piano',
        bestValue: 'MIGLIORE',
        notLoggedIn: 'Devi effettuare il login per attivare Premium',
        notLoggedInCta: 'Accedi / Registrati',
        periods: {
          month: '/mese',
          year: '/anno',
          lifetime: 'una tantum'
        },
        trust: {
          instantAccess: 'Accesso Istantaneo',
          allContent: 'Tutti i Contenuti',
          futureUpdates: 'Aggiornamenti Futuri'
        },
        checkout: {
          proceed: 'Procedi al pagamento',
          redirecting: 'Reindirizzamento al pagamento...',
          securedBy: 'Pagamento sicuro via Stripe',
          cancelled: 'Pagamento annullato',
          activated: 'Premium attivato! 🎉',
          processing: 'Pagamento in elaborazione, ricarica tra qualche secondo.',
          errorPlans: 'Errore nel caricamento dei piani',
          errorAuth: 'Sessione scaduta. Effettua nuovamente il login.',
          errorStart: "Errore durante l'avvio del pagamento",
          errorOpen: 'Impossibile avviare il checkout'
        }
      },
      // Profile
      profile: {
        language: 'Lingua',
        accountStatus: 'Stato Account',
        level: 'Livello'
      },
      // Dashboard
      dashboard: {
        welcome: 'Bentornato',
        continueLeaning: 'Continua ad Imparare',
        quickAccess: 'Accesso Rapido',
        yourLevel: 'Il Tuo Livello',
        recentActivity: 'Attività Recente',
        startToday: 'Inizia la pratica di oggi',
        startTodayDesc: 'Riprendi da dove eri rimasto e continua a migliorare con la chitarra.',
        status: 'Stato',
        upgrade: 'Passa a Premium',
        upgradeProCta: 'Passa a PRO',
        yourProgress: 'I tuoi progressi',
        total: 'Totale',
        advancedStats: 'Statistiche avanzate',
        activeDays: 'Giorni attivi',
        totalTime: 'Tempo totale',
        last7Days: 'Ultimi 7 giorni',
        proOnly: 'Statistiche avanzate disponibili solo per utenti PRO',
        metronomeDesc: 'Timing preciso',
        recorderDesc: 'Registra e salva',
        units: {
          chords: 'accordi',
          exercises: 'esercizi',
          lessons: 'lezioni',
          songs: 'brani',
          tracks: 'basi'
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: ['it', 'en'],
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    detection: {
      // Honour an explicit user choice first (set by the language switcher),
      // then fall back to the browser language so foreign visitors land in
      // English without ever touching the toggle.
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'language',
      caches: ['localStorage'],
    },
  });

export default i18n;
